/**
 *
 * @class BatchCell
 *
 * base class for a simulation result cell
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin
 * @date      March 2022
 *
 */
import {ArtifactType} from "@/models/batch/artifact.type";


export class ResultCell {

  /** unique SWE cell id within it sbatch or result */
  public id: number            = 0;

  /** the SWE batch id */
  public batchId: number       = 0;

  /** the SWE result id,  */
  public resultId: number       = 0;

  /** name of the cell */
  public name:        string   = '';

  /** any metadata for the cell */
  public meta: any             = {};

  /** last update time */
  public updated: Date         = null;

  /** the live progress received so far. */
  public progress: string[]    = [];

  /** the errors (if any) */
  public errors: any[]         = [];

  /** the break-down of pipeline work status for this cell */
  public steps: any            = {};

  /** the overall status of the cell */
  public status: string        = '';

  /** the x ordering */
  public column: number        = 0;

  /** the y ordering */
  public row:    number        = 0;

  /** the tag of the column (traffic) */
  public colTag: number        = 0;

  /** the tag of the row (plant) */
  public rowTag: number        = 0;

  /** final placement of traffic blob */
  public trafficDst: string    = '';

  /** final placement of plant blob */
  public plantDst:    string   = '';

  /** the axis tags */
  public tags: number[]        = [];


  /** if slice failed, and we were able to capture the worker log, track it here */
  public sliceFailure: string  = '';

  /** if sim failed, and we were able to capture the worker log, track it here */
  public simFailure: string  = '';

  /** if KPI failed, and we were able to capture the worker log, track it here */
  public kpiFailure: string  = '';

  /** the downloaded results for the cell */
  public artifacts: Map<ArtifactType, string[]> = new Map<ArtifactType, string[]>();


  /** standard constructor */
  public constructor(init?: Partial<ResultCell>) {

    Object.assign(this, init);
  }

  public findFailures() {

    const rc: ResultCell = this;

    rc.errors.forEach((item: any) => {

      let type: string = '';

      if (!item.hasOwnProperty('detail') || !item.hasOwnProperty('source')) {

        return ;
      }

      const detail: any = item.detail;
      const source: any = item.source;

      if (!source.hasOwnProperty('type') || !detail.hasOwnProperty('single')) {

        return ;
      }

      type = source.type;

      /*
       * single is an object with a property for each hook that ran; key is the screipt path, value
       * is the log.
       *
       */
      const single: any = detail.single;
      let text: string  = '';

      Object.entries(single).forEach(([key, value]) => {

        text += `Script: ${key}\n`;

        if (value.hasOwnProperty('code')) {

          text += `Exit code: ${value['code']}\n`;
        }

        if (value.hasOwnProperty('log')) {

          text += `stdout: ${value['log']}\n`;
        }

      }); /* end of hooks walk */

      switch(type) {

        case 'work_slice': rc.sliceFailure = text; break;
        case 'work_sim':   rc.simFailure   = text; break;
        case 'work_kpi':   rc.kpiFailure   = text; break;
      }

    }); /* end of error walk */

  }

  /** check if any of these artifacts are present */
  public hasAnyArtifact(types: ArtifactType[]): boolean {

    return types.filter(x => this.hasArtifact(x)).length > 0;
  }

  /** check if we even have this artifact */
  public hasArtifact(type: ArtifactType): boolean {

    if (!this.artifacts.has(type)) {

      return false;
    }

    return this.artifacts.get(type).length > 0;
  }

  /** fetch the artifact of this kind */
  public artifact(type: ArtifactType): string|null {

    if (!this.hasArtifact(type)) {

      return null;
    }

    const hits: string[] = this.artifacts.get(type);

    if (hits.length == 0) {

      return null;
    }

    return hits[0];
  }

  /** test if the cell is as complete as its going to be */
  public resolved(): boolean {

    /* all steps must be complete */

    if (this.steps.work_slice < 100.0) {

      return false;
    }

    if (this.steps.work_sim < 100.0) {

      return false;
    }

    return this.steps.work_kpi >= 100.0;
  }

  /** check if the cell passed */
  public completed(): boolean {

    if (!this.resolved()) {

      return false;
    }

    return this.status == 'completed';
  }

  /** check if the cell failed */
  public failed(): boolean {

    if (!this.resolved()) {

      return false;
    }

    switch(this.status) {

      case 'error':
      case 'failed':
      case 'aborted':
      case 'death':
        return true;

      default:
        return false;
    }
  }

  /** check if slicing failed */
  public sliceFailed(): boolean {

    if (!this.resolved()) {

      /* hasn't finished yet, so we don't know for sure if it failed or not */

      return false;
    }

    /* if we have a scenario, it must have worked */

    if (this.hasArtifact(ArtifactType.SCENARIO)) {

      return false;
    }

    /* no scenario means it failed */

    return true;
  }

  public sliceCompleted(): boolean {

    return !this.sliceFailed();
  }

  /** check if sim failed */
  public simFailed(): boolean {

    if (!this.resolved()) {

      /* hasn't finished yet, so we don't know for sure if it failed or not */

      return false;
    }

    /* if we have a train movement, it must have worked */

    if (this.hasArtifact(ArtifactType.MOVEMENT)) {

      return false;
    }

    /* no movement means it failed */

    return true;
  }

  public simCompleted(): boolean {

    return !this.simFailed();
  }

  /** check if kpi failed */
  public kpiFailed(): boolean {

    if (!this.resolved()) {

      /* hasn't finished yet, so we don't know for sure if it failed or not */

      return false;
    }

    /* if we have a KPI, it must have worked */

    if (this.hasArtifact(ArtifactType.KPIX)) {

      return false;
    }

    /* no kpi means it failed */

    return true;
  }

  public kpiCompleted(): boolean {

    return !this.kpiFailed();
  }
}
